import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDragReorder } from "@/hooks/useDragReorder";
import { resolveBilingual } from "@/lib/bilingual";
import { applyPersonalOrder } from "@/lib/reorder";
import { cn } from "@/lib/utils";
import type { FlowNode, LanguagePreference } from "@/types/api";

interface StepOrderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Every non-START/END node in the flow, sorted by the admin's own
   * authored order (fallback when nothing's personalized). */
  nodes: FlowNode[];
  /** This Staff member's saved order, if any — see FlowStepOrder.node_order. */
  order: string[];
  onReorder: (newOrder: string[]) => void;
  onReset: () => void;
  /** Jumps the live call straight to this step and closes the dialog. Each
   * step's own answer buttons still lead exactly where Admin wired them
   * afterward — this only changes which step the CSR is on right now. */
  onJumpTo: (nodeId: string) => void;
  saving: boolean;
  languagePreference: LanguagePreference;
}

/**
 * "My step order" — a Staff member's own drag-reorderable order for an
 * entire flow's steps. Never touches step content or branch routing (see
 * StaffFlowStepOrder); it only decides which step a fresh call starts on
 * (the top of this list — see FlowPlayerPage's firstRealNodeId) and gives
 * a quick way to jump to any step mid-call, in this same order.
 */
export function StepOrderDialog({
  open,
  onClose,
  nodes,
  order,
  onReorder,
  onReset,
  onJumpTo,
  saving,
  languagePreference,
}: StepOrderDialogProps) {
  const { t } = useTranslation();
  const ordered = useMemo(() => applyPersonalOrder(nodes, order), [nodes, order]);
  const hasPersonalOrder = order.length > 0;

  // `disabled: saving` blocks starting a new drag/move while a previous
  // reorder is still saving — otherwise two overlapping saves can race:
  // the second is computed from the still-stale pre-save order, and
  // whichever response lands last silently overwrites the other's actually
  // -intended order (there's no optimistic update or request sequencing
  // beyond this).
  const dragReorder = useDragReorder<FlowNode>({
    items: ordered,
    onCommit: onReorder,
    disabled: saving,
  });

  // The dialog stays mounted (just hidden) between opens — clear any
  // leftover drag state so a stale highlight can't linger into a reopen.
  useEffect(() => {
    if (!open) dragReorder.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset
    // on open/close, not on every render (dragReorder is a fresh object
    // each render).
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("flowPlayer.stepOrderTitle")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{t("flowPlayer.stepOrderDescription")}</p>

        {hasPersonalOrder && (
          <div className="flex items-center gap-2">
            <Badge tone="info">{t("flowPlayer.personalizedOrderBadge")}</Badge>
            <button
              type="button"
              onClick={onReset}
              disabled={saving}
              className="cursor-pointer text-xs font-medium text-slate-500 hover:text-danger hover:underline disabled:pointer-events-none disabled:opacity-50"
            >
              {t("flowPlayer.resetOrder")}
            </button>
          </div>
        )}

        <ol className="min-h-0 flex-1 space-y-1.5 overflow-y-auto">
          {ordered.map((node, i) => {
            const title = resolveBilingual(languagePreference, {
              en: node.title,
              es: node.title_es,
            }).primary;
            const row = dragReorder.dragRowProps(node.id);
            return (
              <li
                key={node.id}
                draggable={row.draggable}
                onDragStart={row.onDragStart}
                onDragOver={row.onDragOver}
                onDragLeave={row.onDragLeave}
                onDrop={row.onDrop}
                onDragEnd={row.onDragEnd}
                className={cn(
                  "flex items-center gap-1 rounded-md",
                  row.draggable && "cursor-grab active:cursor-grabbing",
                  row.isDragging && "opacity-40",
                  row.isDragOver && "ring-2 ring-primary"
                )}
              >
                {ordered.length > 1 && (
                  // Mouse users can drag the row; these buttons are the
                  // only way to reorder on a touch device or via keyboard
                  // (native HTML5 drag-and-drop supports neither). Shown
                  // structurally (not gated on `saving`, same reasoning as
                  // the branch-reorder buttons in FlowPlayerPage) — only
                  // the buttons themselves disable mid-save.
                  <div className="flex shrink-0 flex-col items-center">
                    <button
                      type="button"
                      onClick={() => dragReorder.moveByOffset(node.id, -1)}
                      disabled={i === 0 || saving}
                      aria-label={t("flowPlayer.moveUp")}
                      className="cursor-pointer rounded p-0.5 text-slate-400 hover:text-primary disabled:pointer-events-none disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <GripVertical className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />
                    <button
                      type="button"
                      onClick={() => dragReorder.moveByOffset(node.id, 1)}
                      disabled={i === ordered.length - 1 || saving}
                      aria-label={t("flowPlayer.moveDown")}
                      className="cursor-pointer rounded p-0.5 text-slate-400 hover:text-primary disabled:pointer-events-none disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onJumpTo(node.id);
                    onClose();
                  }}
                  className="app-focus-ring flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-slate-300 bg-surface px-3 py-2 text-left text-sm font-medium text-slate-900 transition-colors duration-150 hover:border-primary hover:bg-primary-subtle"
                >
                  <span className="truncate">{title || t("flowPlayer.stepOrderUntitled")}</span>
                  {i === 0 && (
                    <Badge tone="neutral" className="shrink-0">
                      {t("flowPlayer.stepOrderStartBadge")}
                    </Badge>
                  )}
                </button>
              </li>
            );
          })}
        </ol>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
